import {
  CellClassParams,
  CellDoubleClickedEvent,
  CellFocusedEvent,
  CellKeyDownEvent,
  CellStyle,
  CellValueChangedEvent,
  ColDef,
  EditableCallbackParams,
  GridOptions,
  ITooltipParams,
  RowNode,
  SortChangedEvent,
  ValueFormatterParams,
} from '@ag-grid-community/core'
import { DOCUMENT } from '@angular/common'
import {
  Component,
  ElementRef,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core'
import { FormControl, ValidationErrors, Validators } from '@angular/forms'
import {
  EditableCheckboxRenderer,
  heliosDefaultColDef,
  heliosDefaultColWidth,
  heliosSizingAdjustments,
} from '@helios/ag-grid-config'
import { ExternalTransaction } from '@helios/common-api'
import { BlankSessionService } from '@helios/sessions'
import {
  RootState,
  selectActiveSessionReconDate,
  selectActualReconDate,
  selectFirstFocusedAccountInfo,
  selectFirstLoadedAccountId,
  selectLegacyUserId,
  selectSelectedReconDate,
} from '@helios/state-management'
import { TelemetryService } from '@helios/telemetry'
import {
  UserPreferencesService,
  WorkspaceGridSavedViewService,
} from '@helios/user-preferences'
import {
  NgbActiveModal,
  NgbModal,
  NgbModalRef,
} from '@ng-bootstrap/ng-bootstrap'
import { Store } from '@ngrx/store'
import { AgGridApi, NfTooltipParams } from '@northfork/ag-grid'
import {
  getInlineEditColumnType,
  InlineEditDataType,
  NgGridComponents,
} from '@northfork/ag-grid-angular'
import { composeFieldPath, ISODateFormat, parseDate } from '@northfork/basics'
import { InlineEditDisplayOptions, ToastService } from '@northfork/ng-basics'
import { isAfter } from 'date-fns'
import { Observable, Subject } from 'rxjs'
import { debounceTime, take, takeUntil } from 'rxjs/operators'
import {
  BalanceToAdjust,
  BalanceToAdjustKey,
  balanceToAdjustKeys,
} from '../../../model/balance-to-adjust.model'
import { BulkCashEntryRow } from '../../../model/bulk-transaction/bulk-cash-entry/bulk-cash-entry.model'
import {
  BulkTransactionModalInput,
  BulkTransactionModalMode,
} from '../../../model/bulk-transaction/bulk-popup-input-model'
import {
  BulkSleeveSimpleAccounts,
  SleeveSimple,
} from '../../../model/bulk-transaction/bulk-sleeve-simple-accounts.model'
import {
  BulkCashEntryTransaction,
  ClearwaterTransactionDetailResponse,
  MetaDataTransaction,
  TransactionRequestWrapper,
} from '../../../model/bulk-transaction/bulk-transaction'
import { BulkTransactionRequest } from '../../../model/bulk-transaction/bulk-transaction.model'
import { BulkCashEntryValidationView } from '../../../model/bulk-transaction/bulk-validation-view.model'
import { Currency } from '../../../model/currency.model'
import {
  LoanFeeType,
  LoanFeeTypeKey,
  loanFeeTypeKeys,
} from '../../../model/loan-fee-type.model'
import { SecurityIdentifier } from '../../../model/security-identifier.model'
import { SecurityMasterData } from '../../../model/security.model'
import { AccountInfo } from '../../../model/single-transaction/account-details.model'
import { ExternalUnmatchedTransaction } from '../../../model/single-transaction/external-unmatched-transaction'
import { Transaction } from '../../../model/single-transaction/transaction'
import {
  TranCategory,
  TranCategoryUrl,
} from '../../../model/transaction-category.model'
import {
  TransactionType,
  TransactionTypeEnum,
  TransactionTypeKey,
} from '../../../model/transaction-type.model'
import {
  ErrorSeverity,
  ValidationInfo,
} from '../../../model/validation-response.model'
import { BulkDeleteService } from '../../../services/bulk-transaction/bulk-delete.service'
import { PasteService } from '../../../services/bulk-transaction/paste.service'
import { CopyService } from '../../../services/copy.service'
import { CreateEditTransactionService } from '../../../services/create-edit-transaction.service'
import { ModalFocusService } from '../../../services/modal-focus.service'
import {
  isBulkCopyModal,
  isBulkEditModal,
  isBulkExternalModal,
} from '../../../util/bulk-popup-input-utils'
import {
  BULK_DIALOG_EVENT_NAME,
  bulkMetricsDetail,
  bulkRequestMode,
  customSortComparatorBulk,
  customSortComparatorBulkNumeric,
  customSortComparatorBulkValidatorColumn,
  fillHandleDoubleClickHandler,
  getAccountCellParams,
  isEmptyRow,
  numberValueFormatter,
  onSortChange,
  showOrderIDColumn,
  showTranIdColumn,
} from '../../../util/bulk-specific-common-utils'
import {
  mapBulkExternalTransaction,
  mapCashEntryToBulkTransaction,
} from '../../../util/bulk-transaction-wrapper-util'
import {
  activateDialog,
  CURRENCY_ONLY_TRANSACTION_TYPES,
  deduceValidityStatusCellClass,
  emptySelectedCellsOnValidKeyPress,
  getCashEntryType,
  getDefaultUnknownCounterParty,
  getFieldValidatorFormatter,
  getTransactionTypeCodeFromKey,
  getTransactionTypeNameFromCode,
  isDialogComponentInActive,
  markRowsMissingAccount,
} from '../../../util/common-utils'
import { TransactionConstants } from '../../../util/constants'
import {
  getExternalLoanFields,
  getLoanFieldValues,
} from '../../../util/loan-field.util'
import {
  getCurrencyFromCurrencyCode,
  getCurrencyFromCusip,
  getDefaultCurrency,
} from '../../../util/currency-utils'
import { BulkTransactionModalResponse } from '../bulk-transaction-modal-response'
import { BulkTransactionsSaveConfirmationModalComponent } from '../bulk-transactions-confirm-button-modal/bulk-transactions-confirm-button-modal.component'
import { ErrorTooltipComponent } from '../error-Tooltip/error-tooltip.component'

@Component({
  selector: 'hs-bulk-cash-entry',
  templateUrl: './bulk-cash-entry.component.html',
  styleUrls: ['./bulk-cash-entry.component.scss'],
  providers: [PasteService, CopyService, BulkDeleteService],
})
export class BulkCashEntryComponent implements OnInit, OnDestroy {
  @Input() transInput?: BulkTransactionModalInput

  private readonly onPasteEndBinding = this.onPasteEnd.bind(this)
  private readonly adjustTransactionsPostDeleteBinding =
    this.adjustTransactionsPostDelete.bind(this)

  constructor(
    public activeModal: NgbActiveModal,
    public toastService: ToastService,
    readonly store: Store<RootState>,
    public bulkTransactionService: CreateEditTransactionService,
    private readonly blankSessionService: BlankSessionService,
    private readonly savedViewService: WorkspaceGridSavedViewService,
    private readonly pasteService: PasteService,
    private readonly copyService: CopyService,
    private readonly bulkDeleteService: BulkDeleteService,
    private readonly userPreferencesService: UserPreferencesService,
    private readonly telemetryService: TelemetryService,
    private readonly modalService: NgbModal,
    private readonly elRef: ElementRef,
    private readonly modalFocusService: ModalFocusService,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {
    this.userPreferencesService
      .getBulkDialogSortPromptPreference()
      .pipe(take(1), takeUntil(this.destroySubject$))
      .subscribe(sortable => {
        this.sortable = sortable
        if (this.gridOptions.defaultColDef) {
          this.gridOptions.defaultColDef.sortable = sortable
          this.gridOptions.defaultColDef.comparator = customSortComparatorBulk
        }
      })
    this.modalService.activeInstances
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(activeInstances => {
        this.activeInstances = activeInstances
      })
  }
  sessionAccountInfo?: AccountInfo
  private readonly destroySubject$ = new Subject<void>()
  bulkSleeveSimpleAccount: BulkSleeveSimpleAccounts[] = []
  sleeveSimpleAccount: SleeveSimple[] = []
  sleeveAccountIds: number[] = []
  activeInstances: NgbModalRef[] = []
  sortable = false
  cashEntryData: BulkCashEntryRow[] = []
  currencyList: Currency[] = []
  bulkExternalTransactionDetail: ExternalUnmatchedTransaction[] = []
  private readonly transTypes = getCashEntryType().sort(
    (a: TransactionType, b: TransactionType) => a.name.localeCompare(b.name),
  )

  cashEntryField = composeFieldPath(this.cashEntryData)
  getFieldValidatorFormatter = getFieldValidatorFormatter
  gridOptions: GridOptions = this.createGridOptions()
  isDataValid = true
  isCommonValidationError = false
  disableSaveValidRows = false
  isValidating = false
  formSubmitting = false
  allDataValidated = false
  gridApi?: AgGridApi
  note = new FormControl('')
  reconDate?: Date
  sessionReconDate?: Date
  selectedReconDate?: Date
  private userId?: number
  cellLevelErrorMessageMap = new Map<string, string[]>()
  cellLevelWarningMessageMap = new Map<string, string[]>()
  noteWarning: ValidationErrors | null = null
  currentNoteError: ValidationErrors | null =
    TransactionConstants.DEFAULT_NOTE_VALIDATION_ERROR
  genericErrors: string[] = []
  genericWarnings: string[] = []
  loadingDialog = false
  title = 'Cash Entry Bulk Create'
  disableValidate = true
  deletePostProcessingInProgress = false
  currentRowIndex = 0
  disableClear = false
  originalTransactions: Map<number, BulkCashEntryTransaction> = new Map<
    number,
    BulkCashEntryTransaction
  >()
  externalTransaction: ExternalTransaction[] = []
  securities: Map<string, SecurityIdentifier> = new Map<
    string,
    SecurityIdentifier
  >()
  securityMasterData: Map<string, SecurityMasterData> = new Map<
    string,
    SecurityMasterData
  >()
  clearwaterTransactionDetail: { [s: string]: Transaction } = {}
  isCollateralMap: { [key: number]: boolean } = {}
  isEdit = false
  lastPastedRowIds: string[] = []
  lastColumnIdsPasted: Set<string> = new Set<string>()
  missingTranRowList: Set<string> = new Set<string>()
  loadingText = TransactionConstants.LOAD_TRANSACTION
  errorText = ''
  errorHeader: string | undefined
  errorDetails: unknown
  compId = 'bulk-cash-' + Math.random()
  showConfirmButtonModal = false

  async ngOnInit() {
    const startTime = Date.now()

    activateDialog(this.activeInstances, this.compId, this.elRef)

    this.modalFocusService
      .outsideFocus(this.elRef)
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(() => this.disableGridFocus())

    this.note.valueChanges
      .pipe(debounceTime(500), takeUntil(this.destroySubject$))
      .subscribe(() => {
        this.setNoteErrors()
      })
    if (this.transInput) {
      showTranIdColumn(this.gridOptions, this.transInput, this.sortable)
      if (
        isBulkEditModal(this.transInput) &&
        this.transInput.transactions?.[0].orderId
      ) {
        showOrderIDColumn(this.gridOptions, this.sortable)
      }
    }
    try {
      await this.init()
    } catch (error) {
      this.errorText = TransactionConstants.ERROR_LOADING_TRANSACTION
      if (error instanceof Error) this.errorDetails = error.message
    }
    this.note.markAsTouched()
    this.note.setErrors(this.currentNoteError)

    const now = Date.now()
    const [count, type] = bulkMetricsDetail(
      this.cashEntryData.length,
      this.transInput,
    )
    this.telemetryService.emitCustomEvent(BULK_DIALOG_EVENT_NAME, {
      dialogName: 'Cash-Entry-' + type,
      durationMs: now - startTime,
      tranCount: count,
    })
  }

  private async init() {
    this.loadingDialog = true
    if (this.transInput?.isFromDistriBution)
      this.note.setValue(this.transInput.note)
    this.document.addEventListener('pasteDone', this.onPasteEndBinding)
    this.document.addEventListener(
      'deleteSuccessful',
      this.adjustTransactionsPostDeleteBinding,
    )

    if (this.transInput?.extTranId) {
      await this.loadExtTransactionDetailBulk(this.transInput.extTranId)
    }
    if (this.transInput?.cwTranId)
      await this.loadTransactionDetailBulk(
        this.transInput?.cwTranId,
        this.transInput.securityId,
      )
    await this.loadDataCreate()
    if (this.transInput?.bulkStatus) {
      this.transInput.externalTransaction = mapBulkExternalTransaction(
        this.bulkExternalTransactionDetail,
        this.transInput.bulkStatus,
        this.currencyList,
      )
    }
    if (this.transInput?.cwTranId) {
      this.transInput.transactions = mapCashEntryToBulkTransaction(
        this.clearwaterTransactionDetail,
        this.securityMasterData,
        this.currencyList,
      )
    }

    if (
      !isBulkCopyModal(this.transInput) &&
      (this.transInput?.transactions || this.transInput?.externalTransaction)
    ) {
      this.missingTranRowList.clear()
      this.title = 'Cash Entry Bulk Edit'
      this.disableValidate = false
      this.disableClear = true
      this.isEdit = true
      this.pasteService.setIsEdit()
      if (isBulkEditModal(this.transInput)) {
        this.originalTransactions = new Map(
          this.transInput?.transactions
            ?.filter(tran => tran.legacyOrigTranId)
            .map(tran => [
              tran.legacyOrigTranId as number,
              tran as BulkCashEntryTransaction,
            ]),
        )
        const cashTransactions = this.transInput
          ?.transactions as BulkCashEntryTransaction[]

        this.loadCollateralOnEdit(new Set(this.originalTransactions.keys()))
        cashTransactions.forEach((cashTrans, index) => {
          this.setSecurityData(
            cashTrans.securityId,
            cashTrans.cusip,
            cashTrans.currencyCode,
          )

          this.cashEntryData.push(
            this.generateDataEntryFromCashEntry(cashTrans, index),
          )
        })
      } else if (isBulkExternalModal(this.transInput)) {
        this.externalTransaction = this.transInput
          ?.externalTransaction as ExternalTransaction[]
        if (!this.transInput?.extTranId) {
          const uniqueAccountIdExternal = [
            ...new Set(this.externalTransaction.map(obj => obj.accountId)),
          ]
          await this.loadNonSessionAccountTypeInfo(uniqueAccountIdExternal)
        }
        this.externalTransaction.forEach((extTrans, index) => {
          this.setSecurityData(
            extTrans.securityId,
            extTrans.secIdentifier,
            extTrans.currencyCode,
          )

          this.cashEntryData.push(
            this.generateDataEntryFromExternalCashEntry(extTrans, index),
          )
        })
      }
    } else if (this.transInput?.enterPrediction) {
      this.disableValidate = false
      const cashTransactions = this.transInput
        ?.enterPrediction as BulkCashEntryTransaction[]

      cashTransactions.forEach((cashTrans, index) => {
        this.setSecurityData(
          cashTrans.securityId,
          cashTrans.cusip,
          cashTrans.currencyCode,
        )

        this.cashEntryData.push(
          this.generateDataEntryFromCashEntry(cashTrans, index),
        )
      })
    } else if (isBulkCopyModal(this.transInput)) {
      this.disableValidate = false
      this.originalTransactions = new Map(
        this.transInput?.transactions
          ?.filter(tran => tran.legacyOrigTranId)
          .map(tran => [
            tran.legacyOrigTranId as number,
            tran as BulkCashEntryTransaction,
          ]),
      )
      const cashTransactions = this.transInput
        ?.transactions as BulkCashEntryTransaction[]

      cashTransactions.forEach((cashTrans, index) => {
        this.setSecurityData(
          cashTrans.securityId,
          cashTrans.cusip,
          cashTrans.currencyCode,
        )

        this.cashEntryData.push(
          this.generateDataEntryFromCashEntry(cashTrans, index),
        )
      })

      this.cashEntryData.push(
        this.generateEmptyDataEntry(false, this.cashEntryData.length + 1),
      )
    } else {
      this.createFreshRowData()
    }
    this.gridApi?.api.applyTransaction({ add: this.cashEntryData })

    this.setSleeveSimpleAccountIdsPerRow()

    this.loadingDialog = false
  }

  ngOnDestroy(): void {
    this.pasteService.destroy()
    this.copyService.destroy()
    this.destroySubject$.next()
    this.destroySubject$.complete()

    this.document.removeEventListener('pasteDone', this.onPasteEndBinding)
    this.document.removeEventListener(
      'deleteSuccessful',
      this.adjustTransactionsPostDeleteBinding,
    )
  }

  setSleeveSimpleAccountIdsPerRow() {
    if (this.bulkSleeveSimpleAccount.length > 0)
      this.cashEntryData.forEach(node => {
        node.sleeveSimpleAccountIds = this.bulkSleeveSimpleAccount
          .filter(
            sleeveSimpleId =>
              sleeveSimpleId.id === Number(node.externalAccountId),
          )
          .map(data => data.sleeveSimple)[0]
      })
    else
      this.cashEntryData.forEach(
        node => (node.sleeveSimpleAccountIds = this.sleeveSimpleAccount),
      )
  }

  generateEmptyDataEntry(activated: boolean, id: number): BulkCashEntryRow {
    const tranTypeMap = new Map<string, string[]>()
    tranTypeMap.set('tranType', ['Valid Value Required'])
    if (activated) {
      this.missingTranRowList.add(id.toString())
    }
    return {
      rowId: id.toString(),
      account: '',
      tranType: '',
      cusip: '',
      currency: '',
      entryDate: '',
      settleDate: '',
      postDate: '',
      amount: '',
      validityStatus: activated ? 'invalid' : 'inactive',
      errorMessageMap: activated ? tranTypeMap : new Map(),
      cleared: false,
      collateralBroker: '',
      prepaidInterest: '',
      loanFeeType: undefined,
      balanceToAdjust: undefined,
      exitFee: '',
      exitFeeReceivable: '',
      prepaymentPenaltyFee: '',
      delinquencyFee: '',
      modificationFee: '',
      miscellaneousFee: '',
    }
  }

  onValidityStatusChanged(event: CellValueChangedEvent) {
    if (
      event.node.data.tranType == '' &&
      event.node.data.validityStatus == 'invalid'
    ) {
      this.missingTranRowList.add(event.node.data.rowId)
      this.disableValidate = true
    }
  }

  generateDataEntryFromCashEntry(
    cwTransaction: BulkCashEntryTransaction,
    index: number,
  ): BulkCashEntryRow {
    const cashEntry = {
      rowId: (index + 1).toString(),
      account: cwTransaction.accountId.toString(),
      entryDate: cwTransaction.entryDate,
      settleDate: cwTransaction.settleDate,
      postDate: cwTransaction.postDate,
      amount: cwTransaction.amount ? cwTransaction.amount.toString() : '0.0',
      tranType: getTransactionTypeCodeFromKey(cwTransaction.tranType),
      cusip: cwTransaction.cusip ?? '',
      currency: cwTransaction.currencyCode ?? '',
      validityStatus: 'intermediate',
      errorMessageMap: new Map(),
      cleared: cwTransaction.clearedDate ? true : false,
      collateralBroker: cwTransaction.dataMap?.collateralBroker ?? '',
      isCapitalize:
        cwTransaction.dataMap?.isCapitalize != null
          ? cwTransaction.dataMap.isCapitalize
          : undefined,
      prepaidInterest:
        cwTransaction.dataMap?.prepaidInterest != null
          ? cwTransaction.dataMap.prepaidInterest
          : undefined,
      loanFeeType: cwTransaction.dataMap?.loanFeeType ?? undefined,
      balanceToAdjust: cwTransaction.dataMap?.balanceToAdjust ?? undefined,
      exitFee:
        cwTransaction.dataMap?.exitFee != null
          ? cwTransaction.dataMap.exitFee
          : undefined,
      exitFeeReceivable:
        cwTransaction.dataMap?.exitFeeReceivable != null
          ? cwTransaction.dataMap.exitFeeReceivable
          : undefined,
      prepaymentPenaltyFee:
        cwTransaction.dataMap?.prepaymentPenaltyFee != null
          ? cwTransaction.dataMap.prepaymentPenaltyFee
          : undefined,
      delinquencyFee:
        cwTransaction.dataMap?.delinquencyFee != null
          ? cwTransaction.dataMap.delinquencyFee
          : undefined,
      modificationFee:
        cwTransaction.dataMap?.modificationFee != null
          ? cwTransaction.dataMap.modificationFee
          : undefined,
      miscellaneousFee:
        cwTransaction.dataMap?.miscellaneousFee != null
          ? cwTransaction.dataMap.miscellaneousFee
          : undefined,
    } as BulkCashEntryRow
    if (cwTransaction.legacyOrigTranId) {
      cashEntry.legacyOrigTranId = cwTransaction.legacyOrigTranId
    }
    if (cwTransaction.orderId) {
      cashEntry.orderId = cwTransaction.orderId
    }
    return cashEntry
  }

  generateDataEntryFromExternalCashEntry(
    extTransaction: ExternalTransaction,
    index: number,
  ): BulkCashEntryRow {
    let accountId = extTransaction.accountId.toString()
    if ('CUSTODY' === extTransaction.dataSourceType) {
      if (this.hideAccountForSleeveAggregate(extTransaction.accountId)) {
        accountId = ''
      }
    }
    const externalCashEntry = {
      rowId: (index + 1).toString(),
      account: accountId.toString(),
      entryDate: extTransaction.tradeDate ?? '',
      settleDate: extTransaction.settleDate ?? '',
      postDate: extTransaction.postDate ?? '',
      amount: extTransaction.amount ? extTransaction.amount.toString() : '0.0',
      tranType: extTransaction.tranTypeCode
        ? getTransactionTypeCodeFromKey(
            extTransaction.tranTypeCode as TransactionTypeKey,
          )
        : '',
      cusip: this.adjustCusip(extTransaction) ?? '',
      currency: extTransaction.currencyCode ?? '',
      cleared:
        this.isDateBeforeCurrentReconDate(extTransaction.postDate) ?? false,
      validityStatus: 'intermediate',
      errorMessageMap: new Map(),
      executionId: extTransaction.executionId,
      externalTranId: extTransaction.extTranId,
      externalAccountId: extTransaction.accountId,
      externalCusip: extTransaction.secIdentifier,
      externalTranStatus: extTransaction.status,
      externalDataSourceType: extTransaction.dataSourceType,
      collateralBroker:
        extTransaction.collateralBroker ??
        extTransaction.prometheusJson.aggInfo?.collateralBroker ??
        '',
      isCapitalize: extTransaction.isCapitalize,
      prepaidInterest: extTransaction.prepaidInterest,
      ...getExternalLoanFields(
        extTransaction,
        extTransaction.prometheusJson.aggInfo,
      ),
      transactionTypeLabel: extTransaction.transactionTypeLabel,
      exitFee: extTransaction.exitFee,
      exitFeeReceivable: extTransaction.exitFeeReceivable,
      prepaymentPenaltyFee: extTransaction.prepaymentPenaltyFee,
      delinquencyFee: extTransaction.delinquencyFee,
      modificationFee: extTransaction.modificationFee,
      miscellaneousFee: extTransaction.miscellaneousFee,
    } as BulkCashEntryRow

    if (!extTransaction.tranTypeCode) {
      const tranTypeMap = new Map<string, string[]>()
      tranTypeMap.set('tranType', ['Valid Value Required'])
      this.missingTranRowList.add((index + 1).toString())
      this.disableValidate = true
      externalCashEntry.validityStatus = 'invalid'
      externalCashEntry.errorMessageMap = tranTypeMap
    }
    return externalCashEntry
  }
  adjustCusip(extTransaction: ExternalTransaction): string | undefined {
    if (
      extTransaction.tranTypeCode &&
      CURRENCY_ONLY_TRANSACTION_TYPES.includes(
        TransactionTypeEnum[extTransaction.tranTypeCode as TransactionTypeKey],
      )
    ) {
      const currency = getCurrencyFromCurrencyCode(
        extTransaction.currencyCode,
        this.currencyList,
      )
      if (currency) {
        this.setSecurityData(
          currency.cashSecurityId,
          currency.cusip,
          currency.code,
        )
      }
      return currency?.cusip
    }
    return extTransaction.secIdentifier
  }

  isDateBeforeCurrentReconDate(date: string | undefined) {
    if (
      this.reconDate &&
      !isAfter(parseDate(date, ISODateFormat), this.reconDate)
    )
      return true
    return false
  }

  /** @internal */
  async loadDataCreate() {
    const currencyList$: Observable<Currency[]> =
      this.bulkTransactionService.loadCurrencies()

    const reconDate$ = this.store.select(selectActualReconDate).pipe(take(1))
    const userId$ = this.store.select(selectLegacyUserId).pipe(take(1))
    const sessionReconDate$ = this.store
      .select(selectActiveSessionReconDate)
      .pipe(take(1))
    const selectedReconDate$ = this.store
      .select(selectSelectedReconDate)
      .pipe(take(1))
    return Promise.all([
      reconDate$.toPromise(),
      userId$.toPromise(),
      currencyList$.toPromise(),
      sessionReconDate$.toPromise(),
      selectedReconDate$.toPromise(),
    ]).then(
      ([
        reconDate,
        userId,
        currencyList,
        sessionReconDate,
        selectedReconDate,
      ]) => {
        this.reconDate = reconDate
        this.userId = userId
        this.currencyList = currencyList
        this.sessionReconDate = sessionReconDate
        this.selectedReconDate = selectedReconDate
      },
    )
  }

  private createGridOptions(): GridOptions {
    return {
      ...heliosSizingAdjustments,
      onGridReady: async event => {
        this.gridApi = event
        this.pasteService.registerGrid(event.api, event.columnApi)
        this.copyService.registerGrid(event.api)
        this.bulkDeleteService.registerGrid(event.api)
        await this.savedViewService.manageSavedView(
          event,
          'Bulk-cash-entry-workspace-key',
          this.destroySubject$,
        )
        this.gridApi.api.setSortModel(null)
      },
      getRowNodeId: ({ rowId }: BulkCashEntryRow) => rowId,
      defaultColDef: {
        ...heliosDefaultColDef,
        menuTabs: [],
        sortable: this.sortable,
      },
      sideBar: false,
      rowSelection: 'multiple',
      onCellDoubleClicked: (event: CellDoubleClickedEvent) => {
        const element = event.event?.target as HTMLElement
        if (element.className == 'ag-fill-handle') {
          fillHandleDoubleClickHandler(event.api)
        } else {
          event.api.startEditingCell({
            rowIndex: this.currentRowIndex,
            colKey: event.column.getColId(),
          })
        }
      },
      onCellKeyDown: (event: CellKeyDownEvent) => {
        if (event.api.getEditingCells().length == 0) {
          emptySelectedCellsOnValidKeyPress(
            (event.event as KeyboardEvent).key,
            event.api,
          )
        }
      },
      suppressMultiRangeSelection: false,
      enableCellChangeFlash: true,
      enableRangeSelection: true,
      enableFillHandle: true,
      getContextMenuItems: () => this.getContextMenuItems(),
      columnDefs: this.createColumnDefs(),
      tooltipShowDelay: 0,
      onSortChanged: (params: SortChangedEvent) => onSortChange(params),
      onCellValueChanged: (event: CellValueChangedEvent) => {
        this.onColumnValueChanged(event)
      },
      onCellFocused: (event: CellFocusedEvent) => {
        this.handleCellFocus(event)
      },
    }
  }

  handleCellFocus(event: CellFocusedEvent) {
    this.currentRowIndex = event.rowIndex ?? 0
  }

  /**
   * The right click magic
   *
   * @internal
   */
  getContextMenuItems() {
    return [
      {
        name: 'Remove Row(s)',
        tooltip: 'Remove selected row(s)',
        action: () => {
          this.bulkDeleteService.deleteHandler()
        },
      },
      'export',
    ]
  }

  adjustTransactionsPostDelete() {
    this.deletePostProcessingInProgress = true
    this.missingTranRowList.clear()
    this.disableValidate = true
    this.gridApi?.api.forEachNode(rowNode => {
      if (rowNode.data.validityStatus != 'inactive') {
        if (rowNode.data.tranType == '') {
          this.missingTranRowList.add(rowNode.data.rowId.toString())
        }
      }
    })
    if (this.missingTranRowList.size == 0) this.disableValidate = false
    this.deletePostProcessingInProgress = false
  }

  async onPasteEnd(evt: Event) {
    this.disableValidate = true
    const pasteDetails = (evt as CustomEvent).detail
    const completePastedRowData: BulkCashEntryRow[] = pasteDetails.pastedRowData
    this.lastPastedRowIds = pasteDetails.pastedRowData.map(
      (rowData: { rowId: BulkCashEntryRow }) => rowData.rowId,
    )
    this.lastColumnIdsPasted = pasteDetails.colPastedSet

    const cusipList = new Set<string>()
    completePastedRowData.forEach(rowDataPasted => {
      const cusip = rowDataPasted.cusip
      const transType = getTransactionTypeNameFromCode(rowDataPasted.tranType)

      if (cusip && !this.getSecurityDetailsByCusip(cusip)) {
        if (transType && this.isCurrencyOnlyTransaction(transType)) {
          const currency = getCurrencyFromCusip(cusip, this.currencyList)
          this.setSecurityData(
            currency?.cashSecurityId,
            currency?.cusip,
            currency?.code,
          )
        } else {
          cusipList.add(rowDataPasted.cusip)
        }
      }
    })
    this.loadingDialog = true
    this.loadingText = TransactionConstants.LOAD_SECURITY_INFORMATION
    await this.handleCusipChange([...cusipList])
    this.applyOnChangeForPastedRowNodes()
    if (this.missingTranRowList.size == 0) this.disableValidate = false
    this.loadingDialog = false
  }

  applyOnChangeForPastedRowNodes() {
    this.lastPastedRowIds.forEach(rowId => {
      const displayedNode = this.gridApi?.api.getRowNode(rowId)
      if (displayedNode) {
        this.applyOnChangeForPastedRowNode(displayedNode)
      }
    })
  }

  applyOnChangeForPastedRowNode(displayedNode: RowNode) {
    const allColumns = this.gridApi?.columnApi.getAllColumns()
    allColumns
      ?.filter(column =>
        this.lastColumnIdsPasted.has(column.getColDef().field ?? ''),
      )
      .forEach(currentColumn => {
        const colId = currentColumn.getColDef().field
        this.onColumnValueChanged(
          {
            column: { getColId: () => colId },
            node: displayedNode,
            data: displayedNode.data,
            newValue: displayedNode?.data[colId ?? ''],
            oldValue: '',
          } as unknown as CellValueChangedEvent,
          this.lastColumnIdsPasted,
        )
      })
    if (displayedNode.data.tranType != '') {
      this.missingTranRowList.delete(displayedNode.data.rowId)
    } else {
      this.missingTranRowList.add(displayedNode.data.rowId)
    }
  }

  getSecurityDetailsByCusip(cusip: string): SecurityIdentifier | undefined {
    return this.securities.get(cusip)
  }

  async onColumnValueChanged(
    event: CellValueChangedEvent,
    columnPastedIds?: Set<string>,
  ) {
    /* Category onChange event handling */
    const rowNode = event.node
    const colId = event.column.getColId()
    if (
      rowNode.data[colId] === event.oldValue ||
      this.deletePostProcessingInProgress
    ) {
      return
    }

    const rowData: BulkCashEntryRow = event.data
    if (
      (this.transInput?.modalMode == BulkTransactionModalMode.CreateCWTran ||
        this.transInput?.modalMode == BulkTransactionModalMode.CopyCWTran) &&
      colId != 'validityStatus' &&
      colId != 'errorMessageMap'
    ) {
      if (rowNode?.data.validityStatus == 'inactive') {
        if (colId != 'tranType') {
          const tranTypeMap = new Map<string, string[]>()
          tranTypeMap.set('tranType', ['Valid Value Required'])
          rowNode.data.errorMessageMap = tranTypeMap
          rowNode.setDataValue('validityStatus', 'invalid')
          this.disableValidate = true
        }

        if (
          Number(rowNode?.rowIndex) + 1 ==
          this.gridApi?.api.getDisplayedRowCount()
        ) {
          const rows = [
            this.generateEmptyDataEntry(false, Number(rowNode.data.rowId) + 1),
          ]
          this.gridApi?.api.applyTransaction({ add: rows })
        }
      } else {
        if (
          isEmptyRow(rowNode, [
            'validityStatus',
            'rowId',
            'cleared',
            'errorMessageMap',
            'sleeveSimpleAccountIds',
          ])
        ) {
          rowNode.setDataValue('validityStatus', 'inactive')
          rowNode.data.errorMessageMap = new Map()
          this.missingTranRowList.delete(rowNode.data.rowId)
          if (this.missingTranRowList.size == 0) this.disableValidate = false
        }
      }
    }

    if (
      rowNode.data.tranType != '' &&
      colId != 'validityStatus' &&
      colId != 'errorMessageMap' &&
      rowNode.data.validityStatus != 'intermediate'
    ) {
      rowNode.setDataValue('validityStatus', 'intermediate')
      this.allDataValidated = false
    }

    switch (event.column.getColId()) {
      case 'tranType': {
        if (
          this.isCurrencyOnlyTransaction(
            getTransactionTypeNameFromCode(rowData.tranType),
          )
        ) {
          //allow user to paste currency
          if (!rowData.cusip && !columnPastedIds?.has('currency')) {
            rowData.currency = getDefaultCurrency().code
          }
          if (!isBulkEditModal(this.transInput)) this.handleCurrency(rowData)
        }

        this.missingTranRowList.delete(event.node.data.rowId)
        if (this.missingTranRowList.size == 0) this.disableValidate = false
        break
      }

      case 'currency':
        this.handleCurrency(rowData)
        break
      case 'postDate':
        this.clearUnclearTransaction(event)
        break
      case 'cusip': {
        rowNode.setDataValue('cusip', rowData.cusip.toUpperCase())
        let securityData = this.getSecurityDetailsByCusip(rowData.cusip)
        if (securityData) {
          rowData.currency = securityData.currencyCode
        } else if (
          getTransactionTypeNameFromCode(rowData.tranType) &&
          this.isCurrencyOnlyTransaction(
            getTransactionTypeNameFromCode(rowData.tranType),
          )
        ) {
          const currency = getCurrencyFromCusip(
            rowData.cusip,
            this.currencyList,
          )
          if (currency) {
            this.setSecurityData(
              currency?.cashSecurityId,
              currency?.cusip,
              currency?.code,
            )
            rowData.currency = currency.code
          }
        } else if (!this.loadingDialog) {
          // Don't handle cusip change if loading (i.e. pasting)
          await this.handleCusipChange([rowData.cusip])
          securityData = this.getSecurityDetailsByCusip(rowData.cusip)
          if (securityData) rowData.currency = securityData.currencyCode
        }
        break
      }
      case 'validityStatus':
        this.onValidityStatusChanged(event)
        break
      case 'account':
        if (this.bulkSleeveSimpleAccount.length > 0) {
          this.sleeveSimpleAccount = this.bulkSleeveSimpleAccount
            .filter(data => data.id === Number(rowData.externalAccountId))
            .map(data => data.sleeveSimple)[0]
        }
        if (this.sleeveSimpleAccount.length > 0) {
          const sAccount = this.sleeveSimpleAccount?.filter(
            data => data.displayLabel == rowData.account,
          )[0]?.id
          if (sAccount) {
            rowData.sleeveAccountId = sAccount
          } else {
            rowData.account = ''
          }
        }
    }
    event.node.setData(rowData)
  }

  clearUnclearTransaction(event: CellValueChangedEvent) {
    if (isBulkEditModal(this.transInput)) return

    const postDate = event.node.data['postDate']
    if (
      this.reconDate &&
      !isAfter(parseDate(postDate, ISODateFormat), this.reconDate)
    ) {
      event.node.setDataValue('cleared', true)
    } else event.node.setDataValue('cleared', false)
  }

  private createColumnDefs(): ColDef[] {
    const colDef = [
      {
        field: this.cashEntryField('validityStatus'),
        headerName: '',
        valueGetter: () => '',
        cellClass: (params: { data: { validityStatus: string } }) =>
          deduceValidityStatusCellClass(params.data.validityStatus),
        tooltipField: 'errorMessageMap',
        tooltipComponentParams: {
          showTooltip: params =>
            params?.data.errorMessageMap &&
            params.data.errorMessageMap.size > 0,
        } as NfTooltipParams,
        width: heliosDefaultColWidth.ValidityStatusCol,
        tooltipComponent: ErrorTooltipComponent,
        comparator: customSortComparatorBulkValidatorColumn,
      },
      {
        field: this.cashEntryField('rowNumber'),
        headerName: '#',
        valueGetter: 'node.rowIndex + 1',
        width: heliosDefaultColWidth.SerialNumberCol,
        sortable: false,
      },
      {
        field: this.cashEntryField('errorMessageMap'),
        hide: true,
      },
      {
        field: this.cashEntryField('account'),
        headerName: 'Account ID',
        type: getInlineEditColumnType(),
        width: heliosDefaultColWidth.Id,
        cellRendererParams: {
          editButtonClickOnly: true,
        } as InlineEditDisplayOptions,
        cellEditorSelector: (x: EditableCallbackParams) =>
          getAccountCellParams(
            x,
            !!this.transInput?.externalTransaction,
            this.sessionAccountInfo?.accountType,
          ),

        cellStyle: (params: CellClassParams) => {
          return this.getCellStyle(params)
        },
        tooltipValueGetter: (params: ITooltipParams) => {
          return this.toolTipValueGetter(params)
        },
        tooltipComponentParams: {
          showTooltip: params => this.showToolTip(params),
        } as NfTooltipParams,
        editable: (x: EditableCallbackParams) => this.isAccountEditable(x),
      },
      {
        field: this.cashEntryField('tranType'),
        type: getInlineEditColumnType(),
        cellRendererParams: {
          editButtonClickOnly: true,
        } as InlineEditDisplayOptions,
        headerName: 'Transaction Type*',
        cellEditorParams: {
          validators: [Validators.required],
          dataType: InlineEditDataType.Select,
          options: {
            items: this.transTypes,
            bindLabel: 'code',
            bindValue: 'code',
          },
        },
        cellStyle: (params: CellClassParams) =>
          this.deduceTranTypeCellStyle(params),
        tooltipValueGetter: (params: ITooltipParams) =>
          this.deduceTranTypeToolTip(params),
        tooltipComponentParams: {
          showTooltip: params => this.showToolTip(params),
        } as NfTooltipParams,
      },
      {
        field: this.cashEntryField('entryDate'),
        headerName: 'Entry Date',
        type: getInlineEditColumnType(),
        width: heliosDefaultColWidth.DateCol,
        cellRendererParams: {
          editButtonClickOnly: true,
        } as InlineEditDisplayOptions,
        cellEditorParams: {
          dataType: InlineEditDataType.Date,
        },
        cellStyle: (params: CellClassParams) => {
          return this.getCellStyle(params)
        },
        tooltipValueGetter: (params: ITooltipParams) => {
          return this.toolTipValueGetter(params)
        },
        tooltipComponentParams: {
          showTooltip: params => this.showToolTip(params),
        } as NfTooltipParams,
      },

      {
        field: this.cashEntryField('settleDate'),
        headerName: 'Settle Date',
        type: getInlineEditColumnType(),
        width: heliosDefaultColWidth.DateCol,
        cellRendererParams: {
          editButtonClickOnly: true,
        } as InlineEditDisplayOptions,
        cellEditorParams: {
          dataType: InlineEditDataType.Date,
        },

        cellStyle: (params: CellClassParams) => {
          return this.getCellStyle(params)
        },
        tooltipValueGetter: (params: ITooltipParams) => {
          return this.toolTipValueGetter(params)
        },
        tooltipComponentParams: {
          showTooltip: params => this.showToolTip(params),
        } as NfTooltipParams,
      },
      {
        field: this.cashEntryField('postDate'),
        headerName: 'Post Date',
        type: getInlineEditColumnType(),
        width: heliosDefaultColWidth.DateCol,
        cellRendererParams: {
          editButtonClickOnly: true,
        } as InlineEditDisplayOptions,
        cellEditorParams: {
          dataType: InlineEditDataType.Date,
        },

        cellStyle: (params: CellClassParams) => {
          return this.getCellStyle(params)
        },
        tooltipValueGetter: (params: ITooltipParams) => {
          return this.toolTipValueGetter(params)
        },
        tooltipComponentParams: {
          showTooltip: params => this.showToolTip(params),
        } as NfTooltipParams,
      },
      {
        field: this.cashEntryField('cusip'),
        headerName: 'Asset ID',
        type: getInlineEditColumnType(),
        cellRendererParams: {
          editButtonClickOnly: true,
        } as InlineEditDisplayOptions,
        cellEditorParams: {
          dataType: InlineEditDataType.String,
          disablePopover: true,
        },
        cellStyle: (params: CellClassParams) => {
          return this.getCellStyle(params)
        },
        tooltipValueGetter: (params: ITooltipParams) => {
          return this.toolTipValueGetter(params)
        },
        tooltipComponentParams: {
          showTooltip: params => this.showToolTip(params),
        } as NfTooltipParams,
        editable: (x: EditableCallbackParams) =>
          !isBulkEditModal(this.transInput) && !this.isCurrencyEditable(x),
      },
      {
        field: this.cashEntryField('currency'),
        headerName: 'Currency',
        type: getInlineEditColumnType(),
        cellRendererParams: {
          editButtonClickOnly: true,
        } as InlineEditDisplayOptions,
        cellEditorSelector: () => this.getCurrencyCellParams(),
        cellStyle: (params: CellClassParams) => {
          return this.getCellStyle(params)
        },
        tooltipValueGetter: (params: ITooltipParams) => {
          return this.toolTipValueGetter(params)
        },
        tooltipComponentParams: {
          showTooltip: params => this.showToolTip(params),
        } as NfTooltipParams,
        editable: (x: EditableCallbackParams) =>
          !isBulkEditModal(this.transInput) && this.isCurrencyEditable(x),
      },
      {
        field: this.cashEntryField('amount'),
        headerName: 'Amount',
        type: getInlineEditColumnType(),
        comparator: customSortComparatorBulkNumeric,
        cellRendererParams: {
          editButtonClickOnly: true,
        } as InlineEditDisplayOptions,
        cellEditorParams: {
          dataType: InlineEditDataType.Number,
          disablePopover: true,
        },
        valueFormatter: (params: ValueFormatterParams) =>
          params.value !== ''
            ? numberValueFormatter(Number(params.value), 2, 4)
            : '',
        cellStyle: (params: CellClassParams) => {
          return this.getCellStyle(params)
        },
        tooltipValueGetter: (params: ITooltipParams) => {
          return this.toolTipValueGetter(params)
        },
        tooltipComponentParams: {
          showTooltip: params => this.showToolTip(params),
        } as NfTooltipParams,
      },
      {
        field: this.cashEntryField('cleared'),
        headerName: 'Cleared',
        cellRenderer: EditableCheckboxRenderer,
        width: heliosDefaultColWidth.Id,
      },
      {
        field: this.cashEntryField('collateralBroker'),
        headerName: 'Collateral Broker',
        width: 100,
        type: getInlineEditColumnType(),
        cellEditorParams: {
          dataType: InlineEditDataType.String,
          disablePopover: true,
        },
        cellRendererParams: {
          editButtonClickOnly: true,
        } as InlineEditDisplayOptions,
        cellStyle: (params: CellClassParams) => {
          return this.getCellStyle(params)
        },
        tooltipValueGetter: (params: ITooltipParams) => {
          return this.toolTipValueGetter(params)
        },
        tooltipComponentParams: {
          showTooltip: params => this.showToolTip(params),
        } as NfTooltipParams,
        columnGroupShow: 'open',
        editable: (params: EditableCallbackParams) =>
          this.isCollateralEditable(params),
      },
      {
        field: this.cashEntryField('isCapitalize'),
        headerName: 'Capitalized',
        cellRenderer: EditableCheckboxRenderer,
        width: heliosDefaultColWidth.Id,
      },
      {
        field: this.cashEntryField('prepaidInterest'),
        headerName: 'Loan Prepaid Interest',
        type: getInlineEditColumnType(),
        cellEditorParams: {
          dataType: InlineEditDataType.Number,
          disablePopover: true,
        },
        comparator: customSortComparatorBulkNumeric,
        cellRendererParams: {
          editButtonClickOnly: true,
        } as InlineEditDisplayOptions,
        valueFormatter: (params: ValueFormatterParams) =>
          params.value !== ''
            ? numberValueFormatter(Number(params.value), 2, 2)
            : '',
        cellStyle: (params: CellClassParams) => {
          return this.getCellStyle(params)
        },
        tooltipValueGetter: (params: ITooltipParams) => {
          return this.toolTipValueGetter(params)
        },
        tooltipComponentParams: {
          showTooltip: params => this.showToolTip(params),
        } as NfTooltipParams,
      },
      {
        field: this.cashEntryField('loanFeeType'),
        type: getInlineEditColumnType(),
        cellRendererParams: {
          editButtonClickOnly: true,
        } as InlineEditDisplayOptions,
        headerName: 'Loan Fee Type',
        cellEditorParams: {
          dataType: InlineEditDataType.Select,
          options: {
            items: Object.values(LoanFeeType),
          },
        },
        cellStyle: (params: CellClassParams) => this.getCellStyle(params),
        tooltipValueGetter: () => 'Only applicable to other income',
        tooltipComponentParams: {
          showTooltip: params => this.showToolTip(params),
        } as NfTooltipParams,
        valueFormatter: (params: ValueFormatterParams) =>
          this.getLoanFeeType(params.value)[1],
        editable: (x: EditableCallbackParams) =>
          this.isOtherIncomeTransaction(x) &&
          !this.isCustomLoanFeeType(x.data.loanFeeType),
      },
      {
        field: this.cashEntryField('balanceToAdjust'),
        type: getInlineEditColumnType(),
        cellRendererParams: {
          editButtonClickOnly: true,
        } as InlineEditDisplayOptions,
        headerName: 'Balance To Adjust',
        cellEditorParams: {
          dataType: InlineEditDataType.Select,
          options: {
            items: Object.values(BalanceToAdjust),
          },
        },
        cellStyle: (params: CellClassParams) => this.getCellStyle(params),
        tooltipValueGetter: () =>
          'Only applicable to cash balance field adjustment',
        tooltipComponentParams: {
          showTooltip: params => this.showToolTip(params),
        } as NfTooltipParams,
        valueFormatter: (params: ValueFormatterParams) =>
          this.getBalanceToAdjust(params.value)[1],
        editable: (x: EditableCallbackParams) =>
          this.isCashBalanceAdjustmentTransaction(x),
      },
      {
        field: this.cashEntryField('transactionTypeLabel'),
        hide: true,
      },
      this.makeInlineFeeEditColDef(
        'exitFee',
        'Loan Exit Fee Received',
        customSortComparatorBulkNumeric,
      ),
      this.makeInlineFeeEditColDef(
        'exitFeeReceivable',
        'Loan Exit Fee Receivable',
        customSortComparatorBulkNumeric,
      ),
      this.makeInlineFeeEditColDef(
        'prepaymentPenaltyFee',
        'Loan Prepayment Penalty Fee',
        customSortComparatorBulkNumeric,
      ),
      this.makeInlineFeeEditColDef(
        'delinquencyFee',
        'Loan Delinquency Fee',
        customSortComparatorBulkNumeric,
      ),
      this.makeInlineFeeEditColDef(
        'modificationFee',
        'Loan Modification Fee',
        customSortComparatorBulkNumeric,
      ),
      this.makeInlineFeeEditColDef(
        'miscellaneousFee',
        'Loan Miscellaneous Fee',
        customSortComparatorBulkNumeric,
      ),
      this.makeInlineFeeEditColDef(
        'exitFee',
        'Loan Exit Fee Received',
        customSortComparatorBulkNumeric,
      ),
    ]
    return colDef
  }

  getLoanFeeType(input: string | undefined): [string, string] {
    if (input === undefined || input === null) {
      return ['', '']
    }
    // Check if the input is an ENUM key (loanFeeTypeKeys.includes guards against prototype-chain
    // hits like 'toString', which a bare LoanFeeType[...] lookup would treat as a match).
    if (loanFeeTypeKeys.includes(input as LoanFeeTypeKey)) {
      return [input, LoanFeeType[input as LoanFeeTypeKey]]
    }
    // Check if the input is an ENUM value
    const entry = Object.entries(LoanFeeType).find(
      ([, value]) => value === input,
    )
    // Otherwise it's a custom (fee-master) name — pass it through unchanged so it both displays
    // and round-trips on save instead of being blanked out (GAL-5173).
    return entry ?? [input, input]
  }

  // A custom (fee-master) loanFeeType isn't one of the enum keys/labels, so it has no dropdown
  // option — keep it read-only. Empty and the 4 legacy enum values stay editable (GAL-5173).
  isCustomLoanFeeType(input: string | undefined): boolean {
    if (!input) {
      return false
    }
    // loanFeeTypeKeys.includes (not a bare LoanFeeType[...] lookup) so a name colliding with an
    // inherited prototype property (e.g. 'toString') is still correctly flagged as custom.
    return (
      !loanFeeTypeKeys.includes(input as LoanFeeTypeKey) &&
      !(Object.values(LoanFeeType) as string[]).includes(input)
    )
  }

  isCollateralEditable(params: EditableCallbackParams) {
    return (
      !isBulkEditModal(this.transInput) ||
      this.isCollateralMap?.[params.data.account]
    )
  }

  deduceTranTypeCellStyle(params: CellClassParams) {
    if (
      params.data.tranType == '' &&
      params.data.validityStatus != 'inactive'
    ) {
      return { 'background-color': 'lightcoral' }
    } else {
      return this.getCellStyle(params)
    }
  }

  deduceTranTypeToolTip(params: ITooltipParams) {
    if (
      params.data.tranType == '' &&
      params.data.validityStatus != 'inactive'
    ) {
      return 'Valid Value Required'
    } else {
      return this.toolTipValueGetter(params)
    }
  }

  getCurrencyCellParams() {
    return {
      component: NgGridComponents.inlineEditor,
      params: {
        dataType: InlineEditDataType.Select,
        options: {
          items: this.currencyList,
          bindValue: 'code',
          bindLabel: 'code',
        },
      },
    }
  }

  isCurrencyEditable(params: EditableCallbackParams): boolean {
    return this.isCurrencyOnlyTransaction(
      getTransactionTypeNameFromCode(params.data.tranType),
    )
  }

  isCurrencyOnlyTransaction(tranType: string) {
    if (
      CURRENCY_ONLY_TRANSACTION_TYPES.includes(tranType as TransactionTypeEnum)
    ) {
      return true
    }
    return false
  }

  isOtherIncomeTransaction(params: EditableCallbackParams): boolean {
    return (
      TransactionTypeEnum.OTHER_INCOME ===
      getTransactionTypeNameFromCode(params.data.tranType)
    )
  }

  getBalanceToAdjust(input: string | undefined): [string, string] {
    if (input === undefined || input === null) {
      return ['', '']
    }
    // Check if the input is an ENUM key (balanceToAdjustKeys.includes guards against prototype-chain
    // hits like 'toString', which a bare BalanceToAdjust[...] lookup would treat as a match).
    if (balanceToAdjustKeys.includes(input as BalanceToAdjustKey)) {
      return [input, BalanceToAdjust[input as BalanceToAdjustKey]]
    }
    // Check if the input is an ENUM value
    const entry = Object.entries(BalanceToAdjust).find(
      ([, value]) => value === input,
    )
    // Otherwise pass it through unchanged � a value seeded from an external transaction must not
    // be blanked, or the posted data map ships an empty balanceToAdjust to aws-etl (PATH-4167).
    return entry ?? [input, input]
  }

  isCashBalanceAdjustmentTransaction(params: EditableCallbackParams): boolean {
    return (
      TransactionTypeEnum.CASH_BALANCE_FIELD_ADJUSTMENT ===
      getTransactionTypeNameFromCode(params.data.tranType)
    )
  }

  disableSaveValidRowsButton() {
    let disable = true

    this.gridApi?.api.forEachNode(node => {
      if (
        node.data.validityStatus == 'valid' ||
        node.data.validityStatus == 'warning'
      ) {
        disable = false // If at least one node has validity 'valid' or 'warning', set disable to false
        return // Exit the loop early if validity is 'valid'
      }
    })

    // Set disableSaveValidRows based on the value of disable
    this.disableSaveValidRows = disable
  }

  // True when any active row has no account; gates Validate so account-scoped
  // security lookups are never sent without a real account.
  // Flags active rows with no account as invalid client-side so the user gets an
  // inline "Account is required" error on the cell instead of a silently disabled
  // Validate button. Returns true if any row was missing an account, in which case
  // validate() short-circuits and never sends accountId 0 to the backend.
  markMissingAccountRows(): boolean {
    const anyMissing = markRowsMissingAccount(
      this.gridApi?.api,
      'account',
      'account',
      this.cellLevelErrorMessageMap,
    )
    if (anyMissing) {
      this.isDataValid = false
    }
    return anyMissing
  }

  async validate() {
    this.gridApi?.api?.clearRangeSelection()
    this.isDataValid = true
    this.isCommonValidationError = false
    this.errorDetails = ''
    // A row with no account can't be scoped/validated server-side (it would send
    // accountId 0). Surface it as an inline Account field error and stop here.
    if (this.markMissingAccountRows()) {
      return
    }
    const bulkValidateView = this.createBulkValidationView()
    this.isValidating = true
    try {
      this.loadingText = TransactionConstants.VALIDATE_TRANSACTION
      const validationInfos = await this.bulkTransactionService
        .validateBulkTransaction(bulkValidateView, TranCategoryUrl.CASH_ENTRY)
        .toPromise()
      this.handleBulkValidationResponse(validationInfos.validationMap)
      this.handleCommonValidations(validationInfos.commonValidations)
      this.allDataValidated = true
      this.disableSaveValidRowsButton()
      this.isValidating = false
    } catch (error) {
      this.errorHeader = TransactionConstants.VALIDATION_ERROR
      if (error instanceof Error) this.errorDetails = error.message
      this.isValidating = false
    }
  }

  handleCommonValidations(commonValidations: ValidationInfo[]) {
    this.genericErrors = []
    this.genericWarnings = []
    this.currentNoteError = null
    if (commonValidations.length == 0) {
      this.showConfirmButtonModal = false
    }
    commonValidations.forEach(validationInfo => {
      if (validationInfo.impactedFields.length == 0) {
        validationInfo.impactedFields = ['generic']
      }
      validationInfo.impactedFields.forEach(field => {
        if (field == 'transactionNote') {
          this.isCommonValidationError = true
          this.isDataValid = false
          const validatorKey = 'transactionNoteValidator'
          if (validationInfo.level == ErrorSeverity.ERROR) {
            this.note.markAsTouched()
            this.currentNoteError = {
              [validatorKey]: {
                message: validationInfo.message,
              },
            }
            this.note.setErrors(this.currentNoteError)
          } else if (validationInfo.level == ErrorSeverity.WARN) {
            this.noteWarning = {
              [validatorKey]: {
                message: validationInfo.message,
              },
            }
          }
        } else if (
          field == 'confirmButton' &&
          validationInfo.level == ErrorSeverity.WARN
        ) {
          this.showConfirmButtonModal = true
        } else {
          //generic case
          this.isCommonValidationError = true
          this.isDataValid = false
          if (validationInfo.level == ErrorSeverity.ERROR) {
            this.genericErrors.push(validationInfo.message)
          } else if (validationInfo.level == ErrorSeverity.WARN) {
            this.genericWarnings.push(validationInfo.message)
          }
        }
      })
    })
  }

  showToolTip(params?: ITooltipParams) {
    const impactedFieldColDef = params?.colDef as ColDef
    const impactedFieldProcessed = this.deduceImpactedField(
      impactedFieldColDef.field ?? '',
    )
    const index = params?.data.rowId
    const cellKey = `${index}-${impactedFieldProcessed}`
    const currentValidationErrors = this.cellLevelErrorMessageMap.get(cellKey)
    const currentValidationWarnings =
      this.cellLevelWarningMessageMap.get(cellKey)
    if (
      (currentValidationErrors && currentValidationErrors?.length > 0) ||
      (currentValidationWarnings && currentValidationWarnings?.length > 0) ||
      (params?.data.tranType == '' && params?.data.validityStatus != 'inactive')
    ) {
      return true
    }
    return false
  }

  toolTipValueGetter(params: ITooltipParams): string {
    const impactedFieldColDef = params.colDef as ColDef
    const impactedFieldProcessed = this.deduceImpactedField(
      impactedFieldColDef.field ?? '',
    )
    const index = params.data.rowId
    const cellKey = `${index}-${impactedFieldProcessed}`
    const currentValidationErrors = this.cellLevelErrorMessageMap.get(cellKey)
    const currentValidationWarnings =
      this.cellLevelWarningMessageMap.get(cellKey)
    if (currentValidationErrors) {
      return currentValidationErrors.join(' | ')
    } else if (currentValidationWarnings) {
      return currentValidationWarnings.join(' | ')
    } else {
      return params.value
    }
  }

  getCellStyle(params: CellClassParams): CellStyle {
    const impactedFieldProcessed = this.deduceImpactedField(
      params.colDef.field ?? '',
    )
    const index = params.data.rowId
    const cellKey = `${index}-${impactedFieldProcessed}`
    if (this.cellLevelErrorMessageMap.has(cellKey)) {
      return { 'background-color': 'lightcoral' }
    } else if (this.cellLevelWarningMessageMap.has(cellKey)) {
      return { 'background-color': 'yellow' }
    } else {
      return { 'background-color': '#00000000' }
    }
  }

  deduceImpactedField(impactedFieldFromValidationInfo: string): string {
    if (impactedFieldFromValidationInfo == 'securityId') {
      return 'cusip'
    }
    if (impactedFieldFromValidationInfo == 'accountId') {
      return 'account'
    } else {
      return impactedFieldFromValidationInfo
    }
  }

  handleBulkValidationResponse(bulkValidationInfos: {
    [key: number]: ValidationInfo[]
  }) {
    // create errorMessages column per row and update tooltip with it
    this.gridApi?.api?.forEachNode(rowNode => {
      if (rowNode.data.validityStatus != 'inactive') {
        rowNode.data.validityStatus = 'valid'
        rowNode.data.errorMessageMap = new Map()
      }
    })

    this.note.setErrors(null)
    this.cellLevelErrorMessageMap.clear()
    this.cellLevelWarningMessageMap.clear()
    const bulkValidationInfo = new Map<string, ValidationInfo[]>(
      Object.entries(bulkValidationInfos),
    )
    bulkValidationInfo.forEach((rowValidationInfo: ValidationInfo[], index) => {
      // create a map of key index,field and keep all errors(string or string[](if errors ) and warnings (if only warnings)
      // in cell class rule make it yellow if warning validity status
      // in tooltip use above map to put all errors in tooltip
      const currentRowNode = this.gridApi?.api.getRowNode(index)
      const errorMessages = new Map<string, string[]>()
      const warningMessages = new Map<string, string[]>()

      rowValidationInfo.forEach((validationInfo: ValidationInfo) => {
        const isError = validationInfo.level === ErrorSeverity.ERROR
        const messageMap = isError
          ? this.cellLevelErrorMessageMap
          : this.cellLevelWarningMessageMap
        const messages = isError ? errorMessages : warningMessages

        validationInfo.impactedFields.forEach((impactedField: string) => {
          const impactedFieldProcessed = this.deduceImpactedField(impactedField)
          const currentKey = `${index}-${impactedFieldProcessed}` // More efficient key representation

          // message map
          const msgList = messageMap.get(currentKey) || []
          msgList.push(validationInfo.message)
          messageMap.set(currentKey, msgList)

          // messages
          const fieldMessages = messages.get(impactedFieldProcessed) || []
          fieldMessages.push(validationInfo.message)
          messages.set(impactedFieldProcessed, fieldMessages)
        })
      })

      if (errorMessages.size > 0 && currentRowNode) {
        this.isDataValid = false
        currentRowNode.data.validityStatus = 'invalid'
        currentRowNode.data.errorMessageMap = errorMessages
      }
      if (
        errorMessages.size == 0 &&
        warningMessages.size > 0 &&
        currentRowNode
      ) {
        currentRowNode.data.validityStatus = 'warning'
        currentRowNode.data.errorMessageMap = warningMessages
      }

      if (rowValidationInfo.length == 0 && currentRowNode) {
        currentRowNode.data.validityStatus = 'valid'
        currentRowNode.data.errorMessageMap = new Map()
      }
    })
    this.gridApi?.api.redrawRows()
  }

  createBulkValidationView(): BulkCashEntryValidationView {
    const view: BulkCashEntryValidationView = {
      transaction: this.getGridTransactions(true),
      requestMode: bulkRequestMode(this.transInput?.modalMode),
      reconDate: this.sessionReconDate ?? this.selectedReconDate ?? new Date(),
      tranCategory: TranCategory.CASH_ENTRY,
    }
    if (this.note.value != '') view.transactionNote = this.note.value
    return view
  }

  getGridOnlyValidTransactions(isValidate: boolean) {
    const transactions: TransactionRequestWrapper[] = []
    this.gridApi?.api?.forEachNode(rowNode => {
      if (
        rowNode.data.validityStatus == 'valid' ||
        rowNode.data.validityStatus == 'warning'
      )
        transactions.push(
          this.prepareBulkTransactionData(isValidate, rowNode.data),
        )
    })
    return transactions
  }

  getGridTransactions(isValidate: boolean) {
    const transactions: TransactionRequestWrapper[] = []
    this.gridApi?.api?.forEachNode(rowNode => {
      if (rowNode.data.validityStatus != 'inactive')
        transactions.push(
          this.prepareBulkTransactionData(isValidate, rowNode.data),
        )
    })
    return transactions
  }

  prepareBulkTransactionData(
    isValidate: boolean,
    rowData: BulkCashEntryRow,
  ): TransactionRequestWrapper {
    let currentOriginalTransaction = undefined
    if (!isBulkCopyModal(this.transInput) && rowData.legacyOrigTranId) {
      currentOriginalTransaction = this.originalTransactions.get(
        rowData.legacyOrigTranId,
      )
    }

    const transTypeCode = Object.keys(TransactionTypeEnum).filter(
      x =>
        TransactionTypeEnum[x as TransactionTypeKey] ==
        getTransactionTypeNameFromCode(rowData.tranType),
    )[0] as TransactionTypeKey

    let createdDate = new Date().toISOString()

    if (isBulkEditModal(this.transInput)) {
      if (currentOriginalTransaction?.createdDate) {
        createdDate = new Date(
          currentOriginalTransaction?.createdDate,
        ).toISOString()
      }
    }

    let account = rowData.account
    if (rowData.sleeveAccountId) {
      account = rowData.sleeveAccountId?.toString()
    }

    const transactionData: BulkCashEntryTransaction = {
      postDate: rowData.postDate,
      amount: Number(rowData.amount),
      tranType: transTypeCode,
      userId: this.userId ?? 0,
      accountId: Number(account),
      entryDate: rowData.entryDate,
      settleDate: rowData.settleDate,
      dataMap: this.createDataMap(currentOriginalTransaction, rowData),
      factor: 1,
      isCapitalize: rowData.isCapitalize,
      typeLabel: rowData.transactionTypeLabel,
    }

    if (!isValidate) {
      transactionData.revisionId = currentOriginalTransaction?.revisionId
        ? currentOriginalTransaction.revisionId + 1
        : 1
      transactionData.sourceId = TransactionConstants.HELIOS_SOURCE_ID
      transactionData.createdDate = createdDate
      transactionData.modifiedDate = new Date().toISOString()
      transactionData.active = true
      transactionData.commission = 0
      transactionData.counterParty = getDefaultUnknownCounterParty()
      transactionData.counterPartyId = getDefaultUnknownCounterParty().id
      transactionData.traderId = 0
    }

    if (rowData.cleared) {
      transactionData.clearedDate = isValidate
        ? new Date().toISOString().split('Z')[0]
        : new Date().toISOString()
    }

    let unclearTransaction = false
    if (currentOriginalTransaction?.clearedDate && !rowData.cleared) {
      unclearTransaction = true
    }
    const metaData: MetaDataTransaction = {
      rowId: Number(rowData.rowId),
      reconDate: this.sessionReconDate ?? this.selectedReconDate ?? new Date(),
      cusip: rowData.cusip,
      unclearTransaction: unclearTransaction,
    }

    if (isBulkExternalModal(this.transInput)) {
      metaData.externalAccountId = rowData.externalAccountId
      metaData.executionId = rowData.executionId
      metaData.externalTranId = rowData.externalTranId
      metaData.externalCusip = rowData.externalCusip
      metaData.externalTranStatus = rowData.externalTranStatus
      metaData.dataSourceType = rowData.externalDataSourceType
    }

    if (!isValidate) {
      const securityId = this.getSecurityDetailsByCusip(rowData.cusip)?.id
      if (securityId) transactionData.securityId = securityId
    }

    if (currentOriginalTransaction) {
      transactionData.transactionId = currentOriginalTransaction.transactionId
      transactionData.legacyOrigTranId =
        currentOriginalTransaction.legacyOrigTranId
      transactionData.legacyTranId = currentOriginalTransaction.legacyTranId
    }
    if (!isValidate && currentOriginalTransaction?.typeLabelId) {
      transactionData.typeLabelId = currentOriginalTransaction.typeLabelId
    }

    return { transactionData, metaData }
  }

  createDataMap(
    currentOriginalTransaction: BulkCashEntryTransaction | undefined,
    rowData: BulkCashEntryRow,
  ) {
    const dataMap = {}
    if (currentOriginalTransaction?.dataMap) {
      Object.assign(dataMap, currentOriginalTransaction.dataMap)
    }
    if (rowData.collateralBroker && !currentOriginalTransaction?.dataMap) {
      Object.assign(dataMap, { collateralBroker: rowData.collateralBroker })
    }
    if (rowData.isCapitalize != null) {
      Object.assign(dataMap, { isCapitalize: rowData.isCapitalize })
    }
    if (rowData.prepaidInterest != null) {
      Object.assign(dataMap, {
        prepaidInterest: Number(rowData.prepaidInterest),
      })
    }
    if (rowData.exitFee != null) {
      Object.assign(dataMap, {
        exitFee: Number(rowData.exitFee),
      })
    }
    if (rowData.exitFeeReceivable != null) {
      Object.assign(dataMap, {
        exitFeeReceivable: Number(rowData.exitFeeReceivable),
      })
    }
    if (rowData.prepaymentPenaltyFee != null) {
      Object.assign(dataMap, {
        prepaymentPenaltyFee: Number(rowData.prepaymentPenaltyFee),
      })
    }
    if (rowData.delinquencyFee != null) {
      Object.assign(dataMap, {
        delinquencyFee: Number(rowData.delinquencyFee),
      })
    }
    if (rowData.modificationFee != null) {
      Object.assign(dataMap, {
        modificationFee: Number(rowData.modificationFee),
      })
    }
    if (rowData.miscellaneousFee != null) {
      Object.assign(dataMap, {
        miscellaneousFee: Number(rowData.miscellaneousFee),
      })
    }
    Object.assign(
      dataMap,
      getLoanFieldValues(field => rowData[field]),
    )
    Object.assign(dataMap, {
      loanFeeType: this.getLoanFeeType(rowData.loanFeeType)[0],
    })
    Object.assign(dataMap, {
      balanceToAdjust: this.getBalanceToAdjust(rowData.balanceToAdjust)[0],
    })
    return dataMap
  }

  validateAndSave(isSaveValidRows?: boolean) {
    if (this.showConfirmButtonModal) {
      const modalRef = this.modalService.open(
        BulkTransactionsSaveConfirmationModalComponent,
        { keyboard: false },
      )
      modalRef.result.then((confirmed: boolean) => {
        if (confirmed) {
          return isSaveValidRows ? this.saveValidRows() : this.save()
        } else return
      })
    } else if (isSaveValidRows) this.saveValidRows()
    else this.save()
  }

  async saveValidRows() {
    await this.save(true)
    const newRowData: BulkCashEntryRow[] = []
    this.gridApi?.api.forEachNode(node => {
      if (
        node.data.validityStatus != 'valid' &&
        node.data.validityStatus != 'warning'
      )
        newRowData.push(node.data)
    })
    this.gridApi?.api.setRowData(newRowData)

    this.disableSaveValidRowsButton()
  }

  async save(onlyValidRows?: boolean) {
    if (!this.isDataValid && !onlyValidRows) {
      return
    }
    this.formSubmitting = true
    this.loadingText = TransactionConstants.SAVE_TRANSACTION
    await this.blankSessionService.createBlankSessionIfNotInSession()
    const tranCommand: BulkTransactionRequest = {
      transaction: onlyValidRows
        ? this.getGridOnlyValidTransactions(false)
        : this.getGridTransactions(false),
      tranCategory: TranCategory.CASH_ENTRY,
      requestMode: bulkRequestMode(this.transInput?.modalMode),
    }
    if (this.note.value != '') {
      tranCommand.transactionNote = { newValue: this.note.value }
    }
    if (isBulkEditModal(this.transInput)) {
      tranCommand.notes =
        TransactionConstants.HELIOS_BULK_CASH_ENTRY_SAVE_NOTE_CW_EDIT
      this.bulkTransactionService
        .editBulkTransaction(tranCommand, TranCategoryUrl.CASH_ENTRY)
        .pipe(takeUntil(this.destroySubject$))
        .subscribe(
          () => {
            this.toastService.success(
              'Bulk Transaction edit command successfully submitted.',
            )
            this.formSubmitting = false
            if (!onlyValidRows) this.closeModal(true, tranCommand.transaction)
          },
          error => {
            this.errorHeader = TransactionConstants.ERROR_SAVING_TRANSACTION
            this.errorDetails = error.error
            this.formSubmitting = false
          },
        )
    } else if (isBulkExternalModal(this.transInput)) {
      tranCommand.notes =
        TransactionConstants.HELIOS_BULK_CASH_ENTRY_SAVE_NOTE_EXTERNAL
      this.bulkTransactionService
        .createBulkTransaction(tranCommand, TranCategoryUrl.CASH_ENTRY)
        .pipe(takeUntil(this.destroySubject$))
        .subscribe(
          () => {
            this.toastService.success(
              'Bulk External Transaction command successfully submitted.',
            )
            this.formSubmitting = false
            if (!onlyValidRows) this.closeModal(true, tranCommand.transaction)
          },
          error => {
            this.errorHeader = TransactionConstants.ERROR_SAVING_TRANSACTION
            this.errorDetails = error.error
            this.formSubmitting = false
          },
        )
    } else {
      tranCommand.notes =
        TransactionConstants.HELIOS_BULK_CASH_ENTRY_SAVE_NOTE_CW_CREATE
      this.bulkTransactionService
        .createBulkTransaction(tranCommand, TranCategoryUrl.CASH_ENTRY)
        .pipe(takeUntil(this.destroySubject$))
        .subscribe(
          () => {
            this.toastService.success(
              'Bulk Transaction create command successfully submitted.',
            )
            this.formSubmitting = false
            if (!onlyValidRows) this.closeModal(true, tranCommand.transaction)
          },
          error => {
            this.errorHeader = TransactionConstants.ERROR_SAVING_TRANSACTION
            this.errorDetails = error.error
            this.formSubmitting = false
          },
        )
    }
  }

  clearAll() {
    this.gridApi?.api.applyTransaction({
      remove: this.gridApi?.api.getRenderedNodes().map(rowNode => rowNode.data),
    })

    this.createFreshRowData()
    this.gridApi?.api.applyTransaction({ add: this.cashEntryData })
    this.cellLevelErrorMessageMap.clear()
    this.cellLevelWarningMessageMap.clear()
    this.disableValidate = true
    this.errorDetails = ''
  }
  createFreshRowData() {
    this.cashEntryData = []
    this.cashEntryData.push(this.generateEmptyDataEntry(true, 1))
    for (let i = 1; i <= 9; i++) {
      this.cashEntryData.push(this.generateEmptyDataEntry(false, i + 1))
    }
  }

  handleCurrency(rowData: BulkCashEntryRow) {
    if (this.currencyList) {
      const currency = getCurrencyFromCurrencyCode(
        rowData.currency,
        this.currencyList,
      )
      if (
        currency &&
        this.isCurrencyOnlyTransaction(
          getTransactionTypeNameFromCode(rowData.tranType),
        )
      ) {
        rowData.cusip = currency.cusip
        this.setSecurityData(
          currency.cashSecurityId,
          currency.cusip,
          currency.code,
        )
      }
    }
  }

  setSecurityData(
    securityId: number | undefined,
    cusip: string | undefined,
    currencyCode: string | undefined,
  ) {
    if (!securityId && !cusip && !currencyCode) {
      return
    }
    const securityData = {
      id: securityId,
      cusip: cusip,
      currencyCode: currencyCode,
    } as SecurityIdentifier
    this.securities.set(securityData.cusip, securityData)
  }

  async handleCusipChange(cusipList: string[]) {
    if (cusipList && cusipList.length > 0) {
      const securityData = await this.bulkTransactionService
        .fetchSecurityIdFromCusip(cusipList)
        .toPromise()
      securityData.forEach(security => {
        this.securities.set(security.cusip, security)
      })
    } else {
      await new Promise(f => setTimeout(f, 100))
      return
    }
  }

  setNoteErrors() {
    if (this.currentNoteError != null) {
      this.note.setErrors(this.currentNoteError)
    }
  }

  private closeModal(
    success: boolean,
    transactionRequests: TransactionRequestWrapper[],
  ): void {
    this.activeModal.close({
      success,
      transactionRequests,
    } as BulkTransactionModalResponse)
  }

  async loadExtTransactionDetailBulk(transInput: number[]) {
    const sessionAccountId = await this.store
      .select(selectFirstLoadedAccountId)
      .pipe(take(1))
      .toPromise()
    const transactionDetail$: Observable<ExternalUnmatchedTransaction[]> =
      this.bulkTransactionService.loadExtTransactionDetailBulk(
        transInput,
        sessionAccountId,
      )
    const accountInfo$ = this.store
      .select(selectFirstFocusedAccountInfo)
      .pipe(take(1))
    return Promise.all([
      transactionDetail$.toPromise(),
      accountInfo$.toPromise(),
    ]).then(async ([extTransactionDetail, accountInfo]) => {
      if (this.transInput) {
        // Create a Map with the transaction details keyed by their id for faster lookup
        const transactionDetailById = new Map(
          extTransactionDetail.map(td => [td.externalTransactionId, td]),
        )
        // Sort the transaction details based on the order of transInput
        this.bulkExternalTransactionDetail = transInput
          .map(id => transactionDetailById.get(id))
          .filter((td): td is ExternalUnmatchedTransaction => Boolean(td))
        await this.setSessionAccounInfo(accountInfo)
      }
    })
  }

  async setSessionAccounInfo(accountInfo?: AccountInfo) {
    if (accountInfo) {
      this.sessionAccountInfo = accountInfo
      if ('sleeveAggregate' === accountInfo.accountType) {
        const subAccounts = await this.bulkTransactionService
          .fetchSubAccounts(accountInfo.accountId)
          .toPromise()
        this.sleeveSimpleAccount = subAccounts
      }
    }
  }

  async loadTransactionDetailBulk(
    cwTransId: string[],
    securityId: number[] | undefined,
  ) {
    const transactionDetail$: Observable<ClearwaterTransactionDetailResponse> =
      this.bulkTransactionService.loadTransactionDetailBulk(
        cwTransId,
        securityId,
        TranCategoryUrl.CASH_ENTRY,
      )
    return Promise.all([transactionDetail$.toPromise()]).then(
      ([transactionDetail]) => {
        const unorderedTransactionDetails = transactionDetail.transactionDataMap
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const orderedTransactionDetails: { [key: string]: any } = {}

        // Sort the transaction details based on the order of cwTransid
        for (const id of cwTransId) {
          if (unorderedTransactionDetails[id]) {
            orderedTransactionDetails[id] = unorderedTransactionDetails[id]
          }
        }

        this.clearwaterTransactionDetail = orderedTransactionDetails
        this.securityMasterData = new Map<string, SecurityMasterData>(
          Object.entries(transactionDetail.securityMasterDataMap ?? ''),
        )
      },
    )
  }

  disableGridFocus() {
    this.gridApi?.api.clearRangeSelection()
  }

  async loadNonSessionAccountTypeInfo(
    uniqueAccountId: number[],
  ): Promise<void> {
    const accountInfoPromise = this.bulkTransactionService
      .fetchAccountTypes(uniqueAccountId)
      .toPromise()
    await Promise.all([accountInfoPromise]).then(async accountTypeObj => {
      if (!accountTypeObj) return
      this.sleeveSimpleAccount = Object.entries(accountTypeObj[0])
        .map(([key, value]) => {
          return {
            id: Number(key),
            type: value,
          }
        })
        .filter(data => data.type === 'sleeveAggregate')

      if (this.sleeveSimpleAccount.length > 0) {
        this.sleeveAccountIds = this.sleeveSimpleAccount.map(({ id }) => id)

        const subAccounts = await this.bulkTransactionService
          .fetchAllSubAccounts(this.sleeveAccountIds)
          .toPromise()

        if (!subAccounts) return

        this.bulkSleeveSimpleAccount = Object.entries(subAccounts).map(
          ([key, value]) => {
            return {
              id: Number(key),
              sleeveSimple: value,
            }
          },
        )
      }
    })
  }

  async loadCollateralOnEdit(accounts: Set<number>) {
    const collateralObservable = await this.bulkTransactionService
      .isCollateralBroker(Array.from(accounts))
      .toPromise()
    this.isCollateralMap = collateralObservable
  }

  isAccountEditable(params: EditableCallbackParams): boolean {
    if (
      'sleeveAggregate' === this.sessionAccountInfo?.accountType ||
      this.transInput?.externalTransaction
    ) {
      if ('sleeveAggregate' === this.sessionAccountInfo?.accountType) {
        return this.isSessionAccountEditable()
      } else {
        return this.isNonSessionAccountsEditable(params)
      }
    } else if (!this.isEdit) {
      return true
    }
    return false
  }

  isSessionAccountEditable() {
    return true
  }

  isNonSessionAccountsEditable(params: EditableCallbackParams) {
    if (this.sleeveAccountIds?.includes(params.data['externalAccountId'])) {
      return true
    }
    return false
  }

  hideAccountForSleeveAggregate(accountId: number) {
    if (
      'sleeveAggregate' === this.sessionAccountInfo?.accountType &&
      this.sessionAccountInfo?.accountId == accountId
    ) {
      return true
    } else if (this.sleeveAccountIds?.includes(accountId)) {
      return true
    }
    return false
  }

  @HostListener('click') dialogClicked() {
    if (isDialogComponentInActive(this.compId)) {
      activateDialog(this.activeInstances, this.compId, this.elRef)
    }
  }

  getComponentId() {
    return this.compId
  }

  copyHeaders() {
    const headers = this.gridApi?.columnApi
      .getAllDisplayedColumns()
      .filter(col => col.getColId() !== 'validityStatus')
      .map(col => col.getColDef().headerName)
    let headersTabSeparated = ''
    if (headers) headersTabSeparated = headers.join('\t')

    navigator.clipboard.writeText(headersTabSeparated)
    this.toastService.success('Copied to Clipboard')
  }

  makeInlineFeeEditColDef(
    field: string,
    headerName: string,
    comparator: ColDef['comparator'],
  ): ColDef {
    return {
      field: field,
      headerName: headerName,
      type: getInlineEditColumnType(),
      comparator: comparator,
      cellEditorParams: {
        dataType: InlineEditDataType.Number,
        disablePopover: true,
      },
      cellRendererParams: {
        editButtonClickOnly: true,
      },
      valueFormatter: (params: ValueFormatterParams) =>
        params.value !== ''
          ? numberValueFormatter(Number(params.value), 2, 2)
          : '',
      cellStyle: (params: CellClassParams) => {
        return this.getCellStyle(params)
      },
      tooltipValueGetter: (params: ITooltipParams) => {
        return this.toolTipValueGetter(params)
      },
      tooltipComponentParams: {
        showTooltip: params => this.showToolTip(params),
      } as NfTooltipParams,
    }
  }
}
